import React, { useState } from 'react';
import { X } from 'lucide-react';

interface UnassignMatchConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (note: string) => Promise<void>;
  unassigning: boolean;
}

const UnassignMatchConfirmModal: React.FC<UnassignMatchConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  unassigning,
}) => {
  const [note, setNote] = useState('');

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onConfirm(note);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="unassign-match-modal-title"
      data-testid="unassign-match-confirm-modal"
    >
      <div className="card max-w-md w-full relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-secondary-400 hover:text-white"
          aria-label="Close"
          disabled={unassigning}
        >
          <X className="h-5 w-5" />
        </button>
        <h2 id="unassign-match-modal-title" className="text-lg font-semibold text-white mb-2">
          Unassign this match?
        </h2>
        <p className="text-sm text-secondary-400 mb-4">
          This will remove the selected schedule match from this VOD and return it to pending
          review. The VOD will not be published until a match is selected again.
        </p>
        <p className="text-sm text-secondary-500 mb-4">
          This does not delete the VOD, Twitch metadata, frame verification evidence, or archive
          history.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Note (optional)</span>
            <textarea
              className="input-field min-h-[80px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={unassigning}
              placeholder="Optional note for review history"
              data-testid="unassign-match-note-input"
            />
          </label>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={unassigning}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-secondary text-red-300 border-red-800 hover:bg-red-900/30"
              disabled={unassigning}
              data-testid="unassign-match-confirm-button"
            >
              {unassigning ? 'Unassigning...' : 'Unassign match'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnassignMatchConfirmModal;
